import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { PrinterOutlined, ShopOutlined } from "@ant-design/icons";
import {
  printEstablishmentReport,
  printEstablishmentReportPreview,
  type EstablishmentReport,
  type PaymentBreakdownItem,
  type PreviewEstablishmentReportRequest,
} from "../../../../../api/reports";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { getApiErrorMessage } from "../../../../../utils/translations";
import {
  ModalHeader,
  GhostButton,
  PrimaryButton,
  footerButtonHeight,
} from "../../modalPrimitives";
import { useAppSelector } from "../../../../../store/hooks";
import {
  printReportDocument,
  reportPrintCss,
} from "./reportDocumentPrint";
import { SoldSellablesSection } from "./SoldSellablesSection";
import { ReportDocumentHeading } from "./ReportDocumentHeading";
import { ReportTipsSection } from "./ReportTipsSection";
import { ReportDepositsSection } from "./ReportDepositsSection";
import { hasReportTips, hasUserTip, reportTipsOf } from "../../../../../lib/reportTips";
import { hasUserDeposit } from "../../../../../lib/reportDeposits";
import { EstablishmentUsersBreakdownTable } from "./EstablishmentUsersBreakdownTable";
import { blockIfLicenseReadOnly } from "../../../../../lib/licenseReadOnly";
import { reportFooterColumns } from "./reportFooterColumns";

interface EstablishmentReportPreviewModalProps {
  open: boolean;
  onClose: () => void;
  report: EstablishmentReport | null;
  isPreviewMode?: boolean;
  onConfirmCreate?: () => void;
  isCreating?: boolean;
  /** Тялото, с което е направен прегледът — „Печат ПОС" преди „Нулирай" го
   *  праща отново, за да излезе същият билет (отчет с id още няма). */
  previewRequest?: PreviewEstablishmentReportRequest | null;
}

export const EstablishmentReportPreviewModal = ({
  open,
  onClose,
  report,
  isPreviewMode = false,
  onConfirmCreate,
  isCreating = false,
  previewRequest = null,
}: EstablishmentReportPreviewModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const message = useOrderingMessage();
  const { notifyPrinterError } = usePrinterNotifications();
  const [isPrintingPos, setIsPrintingPos] = useState(false);
  const restaurantName = useAppSelector(
    (s) => s.auth.user?.restaurant_display_name ?? "",
  );

  // Print to the operator's local POS printer via FiscalAPI MQTT.
  // Mirrors the personal-report flow — BE returns 400 if the user has no
  // POS device row, which we surface as a readable toast.
  // Записаният отчет се печата по id, прегледът — по тялото си.
  const reportId = typeof report?.id === "number" ? report.id : null;
  const canPrintPos = isPreviewMode ? previewRequest !== null : reportId !== null;
  const handlePrintPos = async () => {
    if (!canPrintPos || isPrintingPos) return;
    if (blockIfLicenseReadOnly()) return;
    setIsPrintingPos(true);
    try {
      if (isPreviewMode && previewRequest) {
        await printEstablishmentReportPreview(previewRequest);
      } else if (reportId !== null) {
        await printEstablishmentReport(reportId);
      }
      message.success(t("establishmentReport.printPosSuccess"));
    } catch (error: unknown) {
      notifyPrinterError(
        getApiErrorMessage(error, t("establishmentReport.printPosError")),
      );
    } finally {
      setIsPrintingPos(false);
    }
  };

  useEffect(() => {
    if (!open || !isDesktop) return;

    const handleKeyDown = (e: KeyboardEvent) => {

      if (e.key === "Enter") {
        e.preventDefault();
        if (isPreviewMode && onConfirmCreate && !isCreating) {
          onConfirmCreate();
        } else if (!isPreviewMode) {
          // Enter now defaults to POS print (the primary action) —
          // browser print stays available via the secondary button.
          if (canPrintPos && !isPrintingPos) {
            void handlePrintPos();
          }
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPreviewMode, onConfirmCreate, isCreating, onClose, isDesktop, canPrintPos, isPrintingPos, reportId]);

  if (!report) return null;

  const handlePrint = () => {
    if (blockIfLicenseReadOnly()) return;
    printReportDocument();
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD.MM.YYYY");
  };

  const formatTime = (dateString: string | null) => {
    if (!dateString) return "-";
    return dayjs(dateString).format("HH:mm:ss");
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) return "0.00";
    return Number(amount).toFixed(2);
  };

  const formatBillsCount = (count: number | string) => {
    const n = Number(count);
    if (!Number.isFinite(n)) return String(count);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  // Едно място за заглавието — показва се и в хедъра на модала, и вътре в
  // документа (само при печат), за да не се разминават.
  const documentTitle = t("establishmentReport.title", {
    number: report.report_number,
    date: formatDate(report.report_date),
  });
  const documentSubtitle = `${restaurantName} · ${t("establishmentReport.includedPersonalReports", { count: report.total_personal_reports })}`;

  const paymentRows: PaymentBreakdownItem[] | null =
    report.payment_breakdown && report.payment_breakdown.length > 0
      ? report.payment_breakdown
      : null;
  const isEn = (i18n.language ?? "bg").toLowerCase().startsWith("en");
  const pmName = (item: PaymentBreakdownItem) =>
    isEn ? item.name_en : item.name_bg;

  // С бакшиш „Разбивка по потребители" е 9 колони („Бакшиш" и „За
  // отчитане") — на 720px не се събираше, затова тогава модалът е до 960px
  // (таблет — почти целия екран). Без бакшиш е точно досегашният.
  const hasTipColumns = report.users_breakdown.some(
    (user) => hasUserTip(user) || hasUserDeposit(user),
  );
  const width = isMobile
    ? undefined
    : hasTipColumns
      ? "min(960px, calc(100vw - 32px))"
      : isTablet
        ? 640
        : 720;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
      zIndex={isPreviewMode ? 2500 : 1000}
    >
      <style>{reportPrintCss}</style>
      <div className="bg-pos-card font-pos-sans text-pos-ink flex flex-col max-h-[90vh]">
        <ModalHeader
          icon={<ShopOutlined />}
          title={documentTitle}
          subtitle={documentSubtitle}
          onClose={onClose}
          isNarrow={isNarrow}
        />

      <div className="report-doc-paper bg-surface text-ink p-6 overflow-y-auto min-h-0">
        <ReportDocumentHeading
          title={documentTitle}
          subtitle={documentSubtitle}
        />

        <div className="mb-6">
          <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
            {t("establishmentReport.turnoverByPayment")}
          </h3>
          <table className="w-full border-collapse border border-gray-400 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 px-2 py-1 text-left">
                  {t("establishmentReport.paymentType")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("establishmentReport.bills")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-right">
                  {t("establishmentReport.amount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {paymentRows ? (
                paymentRows.map((row) => (
                  <tr
                    key={row.payment_method_id}
                    className={!row.is_active ? "italic text-gray-500" : ""}
                  >
                    <td className="border border-gray-400 px-2 py-1">
                      {pmName(row)}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {formatBillsCount(row.bills_count)}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right">
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1">
                      {t("establishmentReport.cashPayment")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {report.cash_bills_count}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right">
                      {formatCurrency(report.cash_amount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-2 py-1">
                      {t("establishmentReport.cardPayment")}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-center">
                      {report.card_bills_count}
                    </td>
                    <td className="border border-gray-400 px-2 py-1 text-right">
                      {formatCurrency(report.card_amount)}
                    </td>
                  </tr>
                  {/* Без този ред резервната таблица не се връзва с тотала под
                      нея — „По сметка" и персонализираните методи нямат свой ред. */}
                  {Number(report.other_amount) !== 0 && (
                    <tr>
                      <td className="border border-gray-400 px-2 py-1">
                        {t("establishmentReport.otherPayment")}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-center">
                        {formatBillsCount(report.other_bills_count)}
                      </td>
                      <td className="border border-gray-400 px-2 py-1 text-right">
                        {formatCurrency(report.other_amount)}
                      </td>
                    </tr>
                  )}
                </>
              )}
              <tr className="bg-gray-100 font-bold">
                <td className="border border-gray-400 px-2 py-1">
                  {t("common.total")}
                </td>
                <td className="border border-gray-400 px-2 py-1 text-center">
                  {report.total_bills_count}
                </td>
                <td className="border border-gray-400 px-2 py-1 text-right">
                  {formatCurrency(report.total_amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Бакшишът + „Сума за отчитане:" — само когато го има; иначе
            хартията е досегашната (без сума за отчитане). */}
        <ReportTipsSection report={report} withAmountToReport />

        {/* Взетите депозити; „Сума за отчитане:" — тук, когато бакшиш няма
            (иначе вече е под секцията „Бакшиш"). */}
        <ReportDepositsSection
          report={report}
          withAmountToReport={!hasReportTips(reportTipsOf(report))}
        />

        <div className="grid grid-cols-2 gap-4 text-sm mb-6">
          <div>
            <p className="font-semibold">
              {t("establishmentReport.firstLastOrder")}
            </p>
            <p>
              {formatTime(report.first_order_time)} /{" "}
              {formatTime(report.last_order_time)}
            </p>
          </div>
          <div>
            <p className="font-semibold">
              {t("establishmentReport.firstLastBill")}
            </p>
            <p>
              {formatTime(report.first_bill_closed)} /{" "}
              {formatTime(report.last_bill_closed)}
            </p>
          </div>
        </div>

        <EstablishmentUsersBreakdownTable users={report.users_breakdown} />

        <div className="mb-6">
          <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
            {t("establishmentReport.stationBreakdown")}
          </h3>
          <table className="w-full border-collapse border border-gray-400 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 px-2 py-1 text-left">
                  {t("establishmentReport.station")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("establishmentReport.cashPayment")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("establishmentReport.cardPayment")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("establishmentReport.otherPayment")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-center">
                  {t("establishmentReport.totalBills")}
                </th>
                <th className="border border-gray-400 px-2 py-1 text-right">
                  {t("establishmentReport.totalAmount")}
                </th>
              </tr>
            </thead>
            <tbody>
              {report.stations_breakdown.map((station) => (
                <tr key={station.station_id}>
                  <td className="border border-gray-400 px-2 py-1">
                    {station.station_name}
                  </td>
                  <td className="border border-gray-400 px-2 py-1 text-center">
                    {station.cash_bills_count} (
                    {formatCurrency(station.cash_amount)})
                  </td>
                  <td className="border border-gray-400 px-2 py-1 text-center">
                    {station.card_bills_count} (
                    {formatCurrency(station.card_amount)})
                  </td>
                  <td className="border border-gray-400 px-2 py-1 text-center">
                    {formatBillsCount(station.other_bills_count)} (
                    {formatCurrency(station.other_amount)})
                  </td>
                  <td className="border border-gray-400 px-2 py-1 text-center font-semibold">
                    {station.total_bills_count}
                  </td>
                  <td className="border border-gray-400 px-2 py-1 text-right font-semibold">
                    {formatCurrency(station.total_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SoldSellablesSection
          sellables={report.sold_sellables_summary ?? []}
        />

        {report.cancelled_items_by_station.some(
          (station) => station.items.length > 0,
        ) && (
          <div className="mb-6">
            <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
              {t("establishmentReport.cancelledByStation")}
            </h3>
            {report.cancelled_items_by_station.map((station) => (
              <div key={station.station_id} className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-1 mb-1">
                  {station.station_name}
                </h4>
                <table className="w-full border-collapse border border-gray-400 text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-400 px-2 py-1 text-left">
                        {t("common.product")}
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-center">
                        {t("personalReport.qty")}
                      </th>
                      <th className="border border-gray-400 px-2 py-1 text-right">
                        {t("establishmentReport.amount")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {station.items.map((item, index) => (
                      <tr key={index}>
                        <td className="border border-gray-400 px-2 py-1">
                          {item.item_name}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-center">
                          {item.quantity}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-right">
                          {formatCurrency(item.total_amount)}
                        </td>
                      </tr>
                    ))}
                    {station.items.length > 0 && (
                      <tr className="bg-gray-100 font-bold">
                        <td
                          className="border border-gray-400 px-2 py-1"
                          colSpan={2}
                        >
                          {t("establishmentReport.totalForStation", {
                            name: station.station_name,
                          })}
                        </td>
                        <td className="border border-gray-400 px-2 py-1 text-right">
                          {formatCurrency(
                            station.items.reduce(
                              (sum, item) =>
                                sum + (Number(item.total_amount) || 0),
                              0,
                            ),
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

      </div>

      {/* Прегледът включва и още неприключените лични отчети — „Нулирай" първо
          ще ги издаде всичките. Лентата е извън .report-doc-paper: винаги
          видима над бутоните и не влиза в разпечатката. Само в preview режим —
          записаният отчет няма какво да приключва. */}
      {isPreviewMode && (report.unreported_users?.length ?? 0) > 0 && (
        <div className={isNarrow ? "px-4 py-3" : "px-6 py-3"}>
          <div className="rounded-[10px] border border-pos-danger-brd bg-pos-danger-bg px-4 py-2.5 text-[13px] font-medium text-pos-danger-ink">
            {/* <b> частите идват от самия превод — Trans ги рендира. */}
            <Trans
              i18nKey="establishmentReport.resetWillFinalizePersonalReports"
              components={{ b: <b /> }}
            />
          </div>
        </div>
      )}

      <div
        className={`grid border-t border-pos-line2 ${
          isNarrow ? "gap-2.5 px-4 py-3" : "gap-4 px-6 py-3.5"
        } ${reportFooterColumns(isPreviewMode, isNarrow)}`}
      >
          {isPreviewMode ? (
            <>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.cancel")}
                  onClick={onClose}
                  disabled={isCreating}
                  shortcut={isDesktop ? "Esc" : undefined}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.print")}
                  onClick={handlePrint}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("generateReport.printPosButton")}
                  onClick={handlePrintPos}
                  loading={isPrintingPos}
                  disabled={!canPrintPos || isCreating}
                />
              </div>
              <div className="flex">
                <PrimaryButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("generateReport.resetButton")}
                  onClick={() => onConfirmCreate?.()}
                  loading={isCreating}
                  shortcut={isDesktop ? "⏎" : undefined}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.close")}
                  onClick={onClose}
                  shortcut={isDesktop ? "Esc" : undefined}
                />
              </div>
              <div className="flex">
                <GhostButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("common.print")}
                  onClick={handlePrint}
                />
              </div>
              <div className="flex">
                <PrimaryButton
                  height={footerButtonHeight(isNarrow)}
                  label={t("establishmentReport.printPos")}
                  onClick={handlePrintPos}
                  icon={<PrinterOutlined />}
                  loading={isPrintingPos}
                  disabled={!canPrintPos}
                  shortcut={isDesktop ? "⏎" : undefined}
                />
              </div>
            </>
          )}
      </div>
      </div>
    </Modal>
  );
};
